import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Add, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  capitalize,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import { TableComponent } from 'commons/components/Table';
import { BinType, StockTransferStatusEnum } from 'commons/enums';
import { PRIMARY_RED } from 'commons/styles/colors';
import {
  IStockTransfer,
  IStockTransferItemDestination,
  IStockTransferItemProps,
} from 'commons/types';
import {
  getBinRecommendationDestinationAction,
  getBinTypeFromBinName,
  getStockTransferBySkuCodeAction,
  postRemoveStockTransferDestinationBatch,
  postStockTransferPutDestinationBin,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setBinRecommendationDestination } from 'redux-stores/reducers/stockTransfer.reducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { STOCK_TRANSFER_ROUTES } from 'routes/constants';

import { StockTransferItemForm } from './components';
import { StockTransferTaskStyle as S } from './StockTransferTask.style';
import { sourceRecommendationConfig } from './table-configs';
import { destinationSourceConfig } from './table-configs/destination-source-config';

const DESTINATION_BIN_RECOMMENDATION_PAGE_SIZE = 10;

export const StockTransferTaskDestinationPage: React.FC = () => {
  const { pathname } = useLocation();
  const pathSkuId = pathname.split('/').reverse()[0];
  const dispatch = useDispatch<AppDispatch>();
  const { stockTransfer, stockTransferSkuList, binRecommendationDestination } =
    useSelector((state: RootReducerInterface) => state.stockTransfer);

  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [destinationType, setDestinationType] = useState<BinType | null>(null);
  const [destinationPage, setDestinationPage] = useState(1);

  useEffect(() => {
    // clear recommendation bin every render new page
    setDestinationType(null);
    setDestinationPage(1);
    dispatch(setBinRecommendationDestination({ data: [], total: 0 }));
    dispatch(
      getStockTransferBySkuCodeAction({
        skuCode: pathSkuId,
        navigate,
      }),
    );
  }, [pathSkuId, dispatch, navigate]);

  const stockTransferTask: IStockTransfer = useMemo(() => {
    const emptyStockTransferTask = {
      transfer_number: '', // get from api, format: `ST-<sku_code>-<short-uuid>`
      status: StockTransferStatusEnum.TRANSFERRING,
      warehouse_id: 0,
      user: '-',
      name: '-',
      sku_code: pathSkuId,
      source_bin: [],
      destination_bin: [],
      quantity: 0,
    };
    return stockTransfer[pathSkuId] || emptyStockTransferTask;
  }, [pathSkuId, stockTransfer]);

  const sourceBinNames = useMemo(
    () => Array.from(new Set(stockTransferTask.source_bin.map((b) => b.bin))),
    [stockTransferTask.source_bin],
  );

  const [_placedQty, setPlacedQty] = useState(0);
  useEffect(() => {
    const totalPlacedQty = stockTransferTask.destination_bin.reduce(
      (total, source) => total + source.quantity,
      0,
    );
    setPlacedQty(totalPlacedQty);
  }, [stockTransferTask.destination_bin]);

  const [skuIndex, setSkuIndex] = useState(0);
  useEffect(() => {
    setSkuIndex(stockTransferSkuList.findIndex((sku) => sku === pathSkuId));
  }, [pathSkuId, stockTransferSkuList]);

  const [destinations, setDestinations] = useState<
    Partial<IStockTransferItemProps>[]
  >(structuredClone(stockTransferTask.destination_bin));
  useEffect(() => {
    if (
      stockTransferTask.destination_bin &&
      stockTransferTask.destination_bin.length > 0
    ) {
      setDestinations(structuredClone(stockTransferTask.destination_bin));
    } else {
      setDestinations([{}]);
    }
  }, [stockTransferTask.destination_bin]);

  const handleAddBatch = (): void => {
    const newDestinations = structuredClone(destinations);
    newDestinations.push({});
    setDestinations(newDestinations);
  };

  const debouncePostStockTransferReplaceBatch = useDebouncedCallback(
    (skuCode: string, replacedDestination: IStockTransferItemProps) => {
      postRemoveStockTransferDestinationBatch(
        {
          skuCode,
          removedBin: {
            bin: replacedDestination.bin,
            inventory_number: replacedDestination.inventory_number,
          },
        },
        dispatch,
      );
    },
    500,
    { maxWait: 2000 },
  );

  const handleRemoveBatch = (): void => {
    const newDestinations = structuredClone(destinations);
    const removedDestination = newDestinations.pop();

    if (
      removedDestination &&
      removedDestination.bin &&
      removedDestination.inventory_number
    ) {
      postRemoveStockTransferDestinationBatch(
        {
          skuCode: stockTransferTask.sku_code,
          removedBin: {
            bin: removedDestination.bin,
            inventory_number: removedDestination.inventory_number,
          },
        },
        dispatch,
      );
    }
    const totalPlacedQty = newDestinations.reduce(
      (total, dest) => total + (dest.quantity || 0),
      0,
    );

    setDestinations(newDestinations);
    setPlacedQty(totalPlacedQty);
  };

  const handleReplaceBatch = (
    index: number,
    newItem: Partial<IStockTransferItemProps>,
  ): Partial<IStockTransferItemProps> => {
    const newDestinations = structuredClone(destinations);
    const replacedDestination = newDestinations[index];
    if (
      replacedDestination &&
      replacedDestination.bin &&
      replacedDestination.inventory_number &&
      replacedDestination.quantity
    ) {
      debouncePostStockTransferReplaceBatch(
        stockTransferTask.sku_code,
        replacedDestination as IStockTransferItemProps,
      );
    }
    newDestinations[index] = newItem;
    setDestinations(newDestinations);
    return newItem;
  };

  const sourceBatchQuantity = useMemo(() => {
    return stockTransferTask.source_bin.reduce<
      Record<string, { quantity: number; expiry_date: Date; bin_type: string }>
    >((result, bin) => {
      if (result[bin.inventory_number]) {
        return {
          ...result,
          [bin.inventory_number]: {
            ...result[bin.inventory_number],
            quantity: result[bin.inventory_number].quantity + bin.quantity,
          },
        };
      }
      return {
        ...result,
        [bin.inventory_number]: {
          quantity: bin.quantity,
          expiry_date: new Date(bin.expiry_date),
          bin_type: bin.bin_type || '',
        },
      };
    }, {});
  }, [stockTransferTask.source_bin]);

  const debounceValidateInventoryNumber = useDebouncedCallback(
    (params: {
      inventoryNumber: string;
      index: number;
      bin?: string;
      binType?: string;
    }) => {
      const validInventoryNumber = Object.keys(sourceBatchQuantity);
      if (!validInventoryNumber.includes(params.inventoryNumber)) {
        const newDestination: Partial<IStockTransferItemProps> = {
          bin: params.bin,
          bin_type: params.binType,
          inventory_number: params.inventoryNumber,
          expiry_date: undefined,
          quantity: 0,
        };
        const newDestinations = structuredClone(destinations);
        newDestinations[params.index] = newDestination;
        setDestinations(newDestinations);
        dispatch(
          snackbarSetData({
            open: true,
            message:
              'Invalid Inventory Number: Did not exist in picked source bins.',
            color: PRIMARY_RED,
          }),
        );
      }
      if (
        params.bin &&
        stockTransferTask.source_bin.some(
          (sourceBin) =>
            sourceBin.bin === params.bin &&
            sourceBin.inventory_number === params.inventoryNumber,
        )
      ) {
        const newDestination: Partial<IStockTransferItemProps> = {
          bin: '',
          bin_type: '',
          inventory_number: '',
          expiry_date: undefined,
          quantity: 0,
        };
        const newDestinations = structuredClone(destinations);
        newDestinations[params.index] = newDestination;
        setDestinations(newDestinations);
        dispatch(
          snackbarSetData({
            open: true,
            message:
              'Invalid Inventory Number/Bin: Cannot transfer the same batch number to the same bin.',
            color: PRIMARY_RED,
          }),
        );
      }
    },
    500,
    { maxWait: 2000 },
  );

  const debounceValidateBatchQuantity = useDebouncedCallback(
    (params: { inventoryNumber: string; quantity: number }) => {
      const currentSourceBatch = sourceBatchQuantity[params.inventoryNumber];
      if (
        !currentSourceBatch ||
        currentSourceBatch.quantity < params.quantity
      ) {
        dispatch(
          snackbarSetData({
            open: true,
            message:
              'Invalid Quantity: Quantity replenish is greater than quantity picked from source.',
            color: PRIMARY_RED,
          }),
        );
      }
    },
    500,
    { maxWait: 2000 },
  );

  const debounceGetBinType = useDebouncedCallback(
    async (
      skuCode: string,
      index: number,
      item: Partial<IStockTransferItemProps>,
    ) => {
      const { bin, inventory_number: inventoryNumber } = item;
      if (bin) {
        const itemBinType = await getBinTypeFromBinName(
          {
            skuCode,
            bin,
          },
          dispatch,
        );
        const newDestinations = structuredClone(destinations);
        let newDestination: Partial<IStockTransferItemProps>;
        if (itemBinType) {
          newDestination = {
            bin,
            inventory_number: inventoryNumber,
            bin_type: itemBinType,
            expiry_date: item.expiry_date && new Date(item.expiry_date),
            quantity: 0,
          };
        } else {
          newDestination = {
            bin,
            inventory_number: inventoryNumber,
            quantity: 0,
          };
        }
        newDestinations[index] = newDestination;
        setDestinations(newDestinations);
      }
    },
    500,
    {
      maxWait: 2000,
    },
  );

  const handleOnChange = async (
    index: number,
    newItem: Partial<IStockTransferItemProps>,
  ): Promise<Partial<IStockTransferItemProps> | undefined> => {
    const currentDestination = destinations[index];
    if (!currentDestination) {
      return undefined;
    }
    const {
      bin,
      bin_type: binType,
      inventory_number: inventoryNumber,
      quantity,
    } = newItem;
    if (
      bin === currentDestination.bin &&
      binType === currentDestination.bin_type &&
      inventoryNumber === currentDestination.inventory_number &&
      quantity === currentDestination.quantity
    ) {
      return undefined;
    }

    if (bin && bin !== currentDestination.bin) {
      debounceGetBinType(stockTransferTask.sku_code, index, newItem);
    }

    if (
      inventoryNumber &&
      inventoryNumber !== currentDestination.inventory_number
    ) {
      debounceValidateInventoryNumber({ inventoryNumber, bin, index, binType });

      const currentSourceBatch = sourceBatchQuantity[inventoryNumber];
      if (currentSourceBatch) {
        const newExpiryDate = currentSourceBatch.expiry_date;

        if (newExpiryDate) {
          const newDestination: Partial<IStockTransferItemProps> = {
            bin: newItem.bin,
            bin_type: newItem.bin_type,
            inventory_number: newItem.inventory_number,
            expiry_date: newExpiryDate,
            quantity: newItem.quantity,
          };
          const newDestinations = structuredClone(destinations);
          newDestinations[index] = newDestination;
          setDestinations(newDestinations);
          return newDestination;
        }
      }
    }
    if (
      inventoryNumber &&
      quantity &&
      quantity !== currentDestination.quantity
    ) {
      debounceValidateBatchQuantity({ inventoryNumber, quantity });
      const totalPlacedQty = destinations.reduce(
        (total, dest, i) =>
          total + (i === index ? quantity : dest.quantity || 0),
        0,
      );
      setPlacedQty(totalPlacedQty);
    }

    return undefined;
  };

  const debouncePostStockTransferPutDestinationBin = useDebouncedCallback(
    (destinationBin: IStockTransferItemDestination) => {
      const skuCode = stockTransferTask.sku_code;
      postStockTransferPutDestinationBin({ skuCode, destinationBin }, dispatch);
    },
    500,
    { maxWait: 2000 },
  );

  const handleUpdateDestination = (
    index: number,
    newDestination: IStockTransferItemProps,
  ): void => {
    const {
      bin,
      bin_type: binType,
      inventory_number: inventoryNumber,
      expiry_date: expiryDate,
      quantity,
    } = newDestination;
    const currentDestination = destinations[index];
    if (!currentDestination) {
      return;
    }
    if (
      bin === currentDestination.bin &&
      binType === currentDestination.bin_type &&
      inventoryNumber === currentDestination.inventory_number &&
      currentDestination.expiry_date &&
      DateTime.fromJSDate(new Date(expiryDate)).hasSame(
        DateTime.fromJSDate(new Date(currentDestination.expiry_date)),
        'day',
      ) &&
      quantity === currentDestination.quantity
    ) {
      /* same data / no need to update to backend */
      return;
    }
    if (bin && inventoryNumber && expiryDate && quantity) {
      const newDestinations = structuredClone(destinations);
      newDestinations[index] = newDestination;
      setDestinations(newDestinations);

      const isRevokedBin = !!stockTransferTask.source_bin.find(
        (source) => source.inventory_number === inventoryNumber,
      );

      debouncePostStockTransferPutDestinationBin({
        ...newDestination,
        is_revoked_bin: isRevokedBin,
      });
    }
  };

  const handleBackToSkuListPage = (): void => {
    navigate(STOCK_TRANSFER_ROUTES.LIST_DESTINATION);
  };

  const [nextSku, setNextSku] = useState<string | null>(null);
  useEffect(() => {
    const nextSkuIndex = skuIndex + 1;
    if (
      nextSkuIndex >= 0 &&
      nextSkuIndex < stockTransferSkuList.length &&
      stockTransferSkuList[nextSkuIndex]
    ) {
      setNextSku(stockTransferSkuList[nextSkuIndex]);
    } else {
      setNextSku(null);
    }
  }, [stockTransferSkuList, skuIndex]);

  const handleGoToNextSku = (): void => {
    if (nextSku) {
      navigate(
        STOCK_TRANSFER_ROUTES.TASK_DESTINATION.replace(':skuId', nextSku),
      );
    }
  };

  useEffect(() => {
    if (destinationType && sourceBinNames.length > 0) {
      dispatch(
        getBinRecommendationDestinationAction({
          skuCode: pathSkuId,
          binType: destinationType,
          bins: sourceBinNames,
          page: destinationPage,
          pageSize: DESTINATION_BIN_RECOMMENDATION_PAGE_SIZE,
        }),
      );
    }
  }, [destinationType, destinationPage, sourceBinNames, pathSkuId, dispatch]);

  const getDestinationTableData = (value: BinType): void => {
    setDestinationType(value);
    setDestinationPage(1);
  };

  const destinationTotalPages = Math.max(
    1,
    Math.ceil(
      binRecommendationDestination.total /
        DESTINATION_BIN_RECOMMENDATION_PAGE_SIZE,
    ),
  );

  const handleDestinationPrevPage = (): void => {
    setDestinationPage((page) => Math.max(1, page - 1));
  };

  const handleDestinationNextPage = (): void => {
    setDestinationPage((page) => Math.min(destinationTotalPages, page + 1));
  };

  return (
    <>
      {/* Body */}
      <S.ContentWrapper>
        {/* Header Section */}
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="TextSTTaskDestinationSKU">
            {pathSkuId}
          </S.Subtitle>
          <S.SubHeaderText data-testid="TextSTTaskDestinationName">
            {stockTransferTask.name || '-'} <br />
          </S.SubHeaderText>
        </S.SubtitleWrapper>

        {/* Source Bin Section */}
        <S.ItemContentWrapper container>
          <Grid item xs={12}>
            <S.SubHeaderText>Source Bin</S.SubHeaderText>
          </Grid>
          <TableComponent
            data={stockTransferTask.source_bin || []}
            config={destinationSourceConfig}
            additionalTableConfig={{ bodyFontSize: '12px' }}
          />
        </S.ItemContentWrapper>

        {/* Destination Info Section */}
        <S.ItemContentWrapper container>
          <S.ItemContentWrapper container>
            <Grid item xs={12} sm={4} md={3}>
              <S.SubHeaderText>Destination Informations</S.SubHeaderText>
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              md={9}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
              }}
            >
              <FormControl
                sx={{
                  '.MuiInputLabel-root.Mui-focused': {
                    display: 'none', // Hide label on hover
                  },
                }}
              >
                {!destinationType && (
                  <InputLabel sx={{ transition: 'none', fontSize: 14 }}>
                    Select Bin Type
                  </InputLabel>
                )}
                <S.SelectBinType
                  value={destinationType || ''}
                  onChange={(e: SelectChangeEvent) =>
                    getDestinationTableData(e.target.value as BinType)
                  }
                  inputProps={{
                    'data-testid': 'SelectSTTaskDestinationBinType',
                  }}
                >
                  {Object.values(BinType).map((_binType) => (
                    <MenuItem value={_binType}>
                      {capitalize(_binType.replace('_', ' '))}
                    </MenuItem>
                  ))}
                </S.SelectBinType>
              </FormControl>
              {destinationType && binRecommendationDestination.total > 0 && (
                <S.PaginationRow>
                  {!isMobile && (
                    <S.PaginationText>
                      Showing{' '}
                      {(destinationPage - 1) *
                        DESTINATION_BIN_RECOMMENDATION_PAGE_SIZE +
                        1}{' '}
                      to{' '}
                      {Math.min(
                        destinationPage *
                          DESTINATION_BIN_RECOMMENDATION_PAGE_SIZE,
                        binRecommendationDestination.total,
                      )}{' '}
                      of {binRecommendationDestination.total} items
                    </S.PaginationText>
                  )}
                  <S.PaginationControls>
                    <S.PaginationButton
                      disabled={destinationPage <= 1}
                      onClick={handleDestinationPrevPage}
                      data-testid="BtnSTTaskDestinationBinPrevPage"
                    >
                      <ChevronLeft />
                    </S.PaginationButton>
                    <S.PageNumber>{destinationPage}</S.PageNumber>
                    <S.PaginationButton
                      disabled={destinationPage >= destinationTotalPages}
                      onClick={handleDestinationNextPage}
                      data-testid="BtnSTTaskDestinationBinNextPage"
                    >
                      <ChevronRight />
                    </S.PaginationButton>
                  </S.PaginationControls>
                </S.PaginationRow>
              )}
            </Grid>
            <TableComponent
              data={binRecommendationDestination.data}
              config={sourceRecommendationConfig}
              additionalTableConfig={{ bodyFontSize: '12px' }}
            />
          </S.ItemContentWrapper>
        </S.ItemContentWrapper>

        {/* Form Section */}
        {destinations.map((destination, index) => (
          <StockTransferItemForm
            index={index}
            item={destination}
            type="pick"
            onAllFilled={handleUpdateDestination}
            onChanged={handleOnChange}
            onReplace={handleReplaceBatch}
          />
        ))}

        {/* Batch Actions */}
        <Grid container style={{ marginTop: 8 }}>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <S.RedButtonText
              onClick={handleRemoveBatch}
              disabled={destinations.length === 1}
              data-testid="BtnSTTaskDestinationRemoveBatch"
            >
              REMOVE BATCH
            </S.RedButtonText>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'right' }}>
            <S.BlueButtonText
              onClick={handleAddBatch}
              data-testid="BtnSTTaskDestinationAddBatch"
            >
              <Add /> ADD BATCH
            </S.BlueButtonText>
          </Grid>
        </Grid>

        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={handleBackToSkuListPage}
              data-testid="BtnSTTaskDestinationBack"
            >
              BACK TO SKU LIST
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              onClick={handleGoToNextSku}
              disabled={!nextSku}
              data-testid="BtnSTTaskDestinationNext"
            >
              NEXT SKU
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </>
  );
};
